import React, { useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";

import { Helmet } from "react-helmet-async";

import emailjs from "@emailjs/browser";

const ContactPage = () => {
  const formRef = useRef();

  const [toastMessage, setToastMessage] = useState("");

  const [showToast, setShowToast] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    emailjs.init("J3susBTvoh0iktXHL");
  }, []);

  // Toast notification handler

  const showCustomToast = (message) => {
    setToastMessage(message);

    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  // Form submission handler with EmailJS

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      await emailjs.sendForm(
        "service_42jctyg",

        "template_oyzszk1",

        formRef.current,

        "J3susBTvoh0iktXHL",
      );

      showCustomToast("Message sent successfully! I'll get back to you soon.");

      formRef.current.reset();
    } catch (error) {
      showCustomToast("Oops! Something went wrong. Please try again.");

      console.error("EmailJS Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // WhatsApp Link Setup

  const whatsappNumber = "94715252563"; // Sri Lanka country code + number without leading 0

  const message = encodeURIComponent(
    "Hi Hasanka! I saw your photography and would love to chat about a session.",
  );

  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  return (
    <main className="min-h-screen bg-dreamy-cream pt-32 pb-20 px-6">
      <Helmet>
        <title>
          Book a Session | Contact Photographer in Colombo & Kurunegala
        </title>

        <meta
          name="description"
          content="Ready to capture your memories? Contact Hasanka for photography bookings in Colombo, Kurunegala, and across Sri Lanka. Call 078 778 4795 or WhatsApp today."
        />

        <meta
          name="keywords"
          content="Hire photographer Sri Lanka, Wedding photography rates Sri Lanka, Contact Hasanka photographer, Photography Kurunegala contact"
        />

        <meta
          property="og:title"
          content="Contact StillsByHasanka | Let's Create Magic"
        />

        <meta property="og:url" content="https://stillsbyhasanka.com/contact" />
      </Helmet>

      {/* Custom Toast Notification */}

      {showToast && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50"
        >
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-6 py-4 rounded-full shadow-lg shadow-emerald-500/30 flex items-center gap-3 font-sans text-sm font-medium backdrop-blur-sm border border-emerald-300/30">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>

            {toastMessage}
          </div>
        </motion.div>
      )}

      <div className="container mx-auto max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* LEFT SIDE: INFO & SOCIALS */}

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-12"
          >
            <div>
              <h2 className="font-serif text-5xl md:text-7xl text-dreamy-earth mb-6">
                Say <span className="italic text-dreamy-gold">Hello</span>
              </h2>

              <p className="font-sans text-dreamy-earth/60 leading-relaxed max-w-sm">
                Whether it's a wedding, a portrait session, or just a creative
                idea—I'd love to hear from you. Let's create something timeless
                together.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex flex-col gap-1">
                <span className="font-sans text-[10px] tracking-widest uppercase text-dreamy-gold">
                  Email
                </span>

                <a
                  href="mailto:dusktheory.lk@gmail.com"
                  className="font-serif text-2xl text-dreamy-earth hover:text-dreamy-gold transition-colors"
                >
                  dusktheory.lk@gmail.com
                </a>
              </div>

              <div className="flex flex-col gap-1">
                <span className="font-sans text-[10px] tracking-widest uppercase text-dreamy-gold">
                  Phone & WhatsApp
                </span>

                <div className="flex items-center gap-4">
                  <a
                    href="tel:+94715252563"
                    className="font-serif text-2xl text-dreamy-earth"
                  >
                    071 525 2563
                  </a>

                  {/* WhatsApp Floating Link */}

                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-[10px] font-sans tracking-widest uppercase border border-green-100 hover:bg-green-100 transition-all"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.484 8.412-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.309 1.656zm6.29-4.143c1.589.943 3.239 1.441 4.937 1.442 5.33 0 9.671-4.341 9.674-9.673 0-2.585-1.005-5.016-2.831-6.842-1.825-1.826-4.257-2.831-6.842-2.831-5.334 0-9.675 4.341-9.677 9.674-.001 1.83.511 3.613 1.482 5.169l-.973 3.551 3.632-.952z" />
                    </svg>
                    Chat Now
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT SIDE: THE FORM */}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white backdrop-blur-sm p-8 md:p-12 rounded-[40px] shadow-sm border border-dreamy-gold/20"
          >
            <form ref={formRef} className="space-y-8" onSubmit={handleSubmit}>
              <div className="flex flex-col gap-2">
                <label className="font-sans text-[10px] tracking-widest uppercase text-dreamy-earth/50 ml-1">
                  Your Name *
                </label>

                <input
                  name="name"
                  required
                  type="text"
                  placeholder="type your name here..."
                  className="bg-transparent border-b border-dreamy-gold/30 py-3 px-1 outline-none focus:border-dreamy-gold transition-colors font-serif text-lg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col gap-2">
                  <label className="font-sans text-[10px] tracking-widest uppercase text-dreamy-earth/50 ml-1">
                    Phone Number *
                  </label>

                  <input
                    name="number"
                    required
                    type="tel"
                    placeholder="07x xxxx xxx"
                    className="bg-transparent border-b border-dreamy-gold/30 py-3 px-1 outline-none focus:border-dreamy-gold transition-colors font-serif text-lg"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="font-sans text-[10px] tracking-widest uppercase text-dreamy-earth/50 ml-1">
                    Email (Optional)
                  </label>

                  <input
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    className="bg-transparent border-b border-dreamy-gold/30 py-3 px-1 outline-none focus:border-dreamy-gold transition-colors font-serif text-lg"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-sans text-[10px] tracking-widest uppercase text-dreamy-earth/50 ml-1">
                  Tell me about your vision
                </label>

                <textarea
                  name="message"
                  rows="4"
                  placeholder="Tell me a little about what you're looking for..."
                  className="bg-transparent border-b border-dreamy-gold/30 py-3 px-1 outline-none focus:border-dreamy-gold transition-colors font-serif text-lg resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-dreamy-earth text-dreamy-cream rounded-full font-sans text-xs tracking-[0.3em] uppercase hover:bg-dreamy-gold hover:text-dreamy-earth transition-all duration-500 shadow-lg shadow-dreamy-earth/10 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
